import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, query, where, setDoc, doc, Timestamp, serverTimestamp } from "firebase/firestore";
import * as XLSX from "xlsx";
import db from "./Firebase";

const batchedPhoneSet = async (collName, field, phones) => {
    const clean = [...new Set(phones.filter(Boolean))];
    if (!clean.length) return new Set();
    const found = new Set();
    for (let i = 0; i < clean.length; i += 10) {
        const chunk = clean.slice(i, i + 10);
        const snap = await getDocs(query(collection(db, collName), where(field, "in", chunk)));
        snap.forEach(d => found.add(String(d.data()[field] || "").replace(/\D/g, "").slice(-10)));
    }
    return found;
};

const batchedNameMap = async (phones) => {
    const clean = [...new Set(phones.filter(Boolean))];
    if (!clean.length) return {};
    const map = {};
    for (let i = 0; i < clean.length; i += 10) {
        const chunk = clean.slice(i, i + 10);
        const snap = await getDocs(query(collection(db, "personal_details"), where("phone_no", "in", chunk)));
        snap.forEach(d => {
            const pd = d.data();
            const ph = String(pd.phone_no || "").replace(/\D/g, "").slice(-10);
            if (ph) map[ph] = pd.full_name || pd.name || pd.username || "";
        });
    }
    return map;
};

const parseDate = (val) => {
    if (!val) return null;
    if (typeof val?.toDate === "function") return val.toDate();
    if (val instanceof Date) return val;
    if (typeof val?.seconds === "number") return new Date(val.seconds * 1000);
    const d = new Date(val);
    return isNaN(d) ? null : d;
};

const fmtDate = (val) => {
    const d = parseDate(val);
    if (!d) return "-";
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const toFirestoreTimestamp = (val) => {
    if (!val) return null;
    if (typeof val?.toDate === "function") return val; // already Firestore Timestamp
    if (typeof val?.seconds === "number") return new Timestamp(val.seconds, val.nanoseconds || 0);
    const d = parseDate(val);
    return d ? Timestamp.fromDate(d) : null;
};

const compactPhone = (val) => String(val || "").replace(/\D/g, "").slice(-10);
const cleanText = (val, fb = "-") => (val === null || val === undefined || val === "" ? fb : String(val));

export default function GlowActiveUsers() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [enrolling, setEnrolling] = useState(new Set());

    useEffect(() => {
        const fetchAll = async () => {
            setLoading(true);
            try {
                const snap = await getDocs(collection(db, "nv_glow_membership"));
                const today = new Date();
                const data = [];
                snap.forEach(docSnap => {
                    const d = docSnap.data();
                    const endDate = parseDate(d.end_date);
                    if (!endDate || endDate <= today) return;
                    data.push({
                        id: docSnap.id,
                        phone: compactPhone(d.phone_no),
                        name: "",
                        membershipType: cleanText(d.membership_type),
                        price: d.price ?? "-",
                        startDate: d.start_date || null,
                        endDate: d.end_date || null,
                        createdDate: d.created_date || null,
                        status: cleanText(d.status),
                    });
                });
                data.sort((a, b) => (parseDate(b.endDate)?.getTime() || 0) - (parseDate(a.endDate)?.getTime() || 0));

                const allPhones = data.map(r => r.phone);

                // Check Shiksha membership and fetch names in parallel
                const [shikshaPhones, nameMap] = await Promise.all([
                    batchedPhoneSet("nv_shiksha_subscriptions", "phone_no", allPhones),
                    batchedNameMap(allPhones),
                ]);

                data.forEach(r => {
                    r.isShiksha = shikshaPhones.has(r.phone);
                    r.name = nameMap[r.phone] || "";
                });

                setRows(data);
            } catch (err) {
                console.error("GlowActiveUsers fetch error:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, []);

    const handleEnroll = async (row) => {
        if (enrolling.has(row.phone)) return;
        setEnrolling(prev => new Set([...prev, row.phone]));
        try {
            const isMax = row.membershipType.toLowerCase().includes("max");
            const shikshaType = isMax ? "shiksha_plus_yearly" : "shiksha_plus_monthly";
            const shikshaPrice = isMax ? 1999 : 199;
            const now = Timestamp.now();
            await setDoc(doc(db, "nv_shiksha_subscriptions", row.phone), {
                created_date: serverTimestamp(),
                end_date: toFirestoreTimestamp(row.endDate),
                membership_type: shikshaType,
                name: row.name || "",
                phone_no: row.phone,
                price: shikshaPrice,
                start_date: toFirestoreTimestamp(row.startDate),
                status: "active",
                tag: "shiksha_plus",
                subscription_log: [
                    {
                        created_date: now,
                        membership_type: shikshaType,
                        price: shikshaPrice,
                        tag: "shiksha_plus",
                    },
                ],
            });
            // Update row in state so button changes to Shiksha User
            setRows(prev => prev.map(r => r.phone === row.phone ? { ...r, isShiksha: true } : r));
        } catch (err) {
            console.error("Enroll error:", err);
            alert("Enrollment failed. Please try again.");
        } finally {
            setEnrolling(prev => { const s = new Set(prev); s.delete(row.phone); return s; });
        }
    };

    const filtered = rows.filter(r => {
        const q = search.trim().toLowerCase();
        if (!q) return true;
        return r.phone.includes(q) || r.membershipType.toLowerCase().includes(q) || r.status.toLowerCase().includes(q);
    });

    const handleExport = () => {
        const sheetData = filtered.map((r, i) => ({
            "#": i + 1,
            "Phone No": r.phone,
            "Name": r.name,
            "Membership Type": r.membershipType,
            "Price": r.price,
            "Start Date": fmtDate(r.startDate),
            "End Date": fmtDate(r.endDate),
            "Status": r.status,
            "Shiksha": r.isShiksha ? "Shiksha User" : "Not Shiksha User",
            "Created Date": fmtDate(r.createdDate),
        }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheetData), "Active Glow Members");
        XLSX.writeFile(wb, "glow_active_users.xlsx");
    };

    return (
        <div className="container-fluid py-4">

            {/* HEADER */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h3 className="mb-1">Glow Active Subscriptions</h3>
                    <div className="text-muted" style={{ fontSize: "13px" }}>
                        nv_glow_membership · users whose end_date is still in the future
                    </div>
                </div>
                <Link to="/" className="btn btn-outline-secondary btn-sm">Back</Link>
            </div>

            {/* SUMMARY CARDS */}
            {!loading && (
                <div className="row g-3 mb-3">
                    <div className="col-md-3">
                        <div className="card border-0 shadow-sm text-center py-3" style={{ borderRadius: "12px" }}>
                            <div style={{ fontSize: "2rem", fontWeight: 800, color: "#4361ee" }}>{rows.length}</div>
                            <div style={{ fontSize: "12px", color: "#94a3b8" }}>Total Active Members</div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card border-0 shadow-sm text-center py-3" style={{ borderRadius: "12px" }}>
                            <div style={{ fontSize: "2rem", fontWeight: 800, color: "#0d9488" }}>
                                {rows.filter(r => !r.isShiksha).length}
                            </div>
                            <div style={{ fontSize: "12px", color: "#94a3b8" }}>Not Enrolled in Shiksha</div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card border-0 shadow-sm text-center py-3" style={{ borderRadius: "12px" }}>
                            <div style={{ fontSize: "2rem", fontWeight: 800, color: "#7c3aed" }}>
                                {rows.filter(r => r.membershipType.toLowerCase().includes("max")).length}
                            </div>
                            <div style={{ fontSize: "12px", color: "#94a3b8" }}>Glow Max (Yearly)</div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card border-0 shadow-sm text-center py-3" style={{ borderRadius: "12px" }}>
                            <div style={{ fontSize: "2rem", fontWeight: 800, color: "#d97706" }}>
                                {rows.filter(r => !r.membershipType.toLowerCase().includes("max")).length}
                            </div>
                            <div style={{ fontSize: "12px", color: "#94a3b8" }}>Glow (Monthly)</div>
                        </div>
                    </div>
                </div>
            )}

            {/* SEARCH + EXPORT */}
            <div className="card mb-3">
                <div className="card-body py-3 px-4">
                    <div className="d-flex gap-3 align-items-center flex-wrap">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search by phone, membership type or status…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            style={{ maxWidth: "340px" }}
                        />
                        {search && (
                            <span className="text-muted" style={{ fontSize: "13px" }}>
                                {filtered.length} result{filtered.length !== 1 ? "s" : ""}
                            </span>
                        )}
                        <button
                            className="btn btn-success ms-auto"
                            onClick={handleExport}
                            disabled={loading || filtered.length === 0}
                            style={{ minWidth: "140px", fontWeight: 700 }}
                        >
                            Export Excel
                        </button>
                    </div>
                </div>
            </div>

            {/* TABLE */}
            {loading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status" />
                    <div className="mt-2 text-muted" style={{ fontSize: "14px" }}>Loading…</div>
                </div>
            ) : (
                <div className="card">
                    <div className="table-responsive">
                        <table className="table table-bordered table-hover align-middle mb-0" style={{ fontSize: "13px" }}>
                            <thead className="table-dark">
                                <tr>
                                    <th>#</th>
                                    <th>Phone No</th>
                                    <th>Name</th>
                                    <th>Membership Type</th>
                                    <th className="text-end">Price</th>
                                    <th>Start Date</th>
                                    <th>End Date</th>
                                    <th className="text-center">Status</th>
                                    <th className="text-center">Shiksha</th>
                                    <th className="text-center">Action</th>
                                    <th>Created Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan="11" className="text-center py-4 text-muted">
                                            {search ? "No results found" : "No active Glow members found"}
                                        </td>
                                    </tr>
                                ) : filtered.map((row, i) => (
                                    <tr key={row.id}>
                                        <td>{i + 1}</td>
                                        <td>{row.phone}</td>
                                        <td>{row.name || "-"}</td>
                                        <td>{row.membershipType}</td>
                                        <td className="text-end fw-bold" style={{ color: "#16a34a" }}>
                                            {row.price !== "-" ? `₹${row.price}` : "-"}
                                        </td>
                                        <td style={{ whiteSpace: "nowrap" }}>{fmtDate(row.startDate)}</td>
                                        <td style={{ whiteSpace: "nowrap" }}>
                                            <span className="badge bg-success" style={{ fontSize: "12px", padding: "5px 10px" }}>
                                                {fmtDate(row.endDate)}
                                            </span>
                                        </td>
                                        <td className="text-center">
                                            <span className="badge bg-primary" style={{ fontSize: "12px", padding: "5px 10px" }}>
                                                {row.status}
                                            </span>
                                        </td>
                                        <td className="text-center">
                                            <span className={`badge ${row.isShiksha ? "bg-success" : "bg-secondary"}`}
                                                style={{ fontSize: "12px", padding: "5px 10px" }}>
                                                {row.isShiksha ? "✓ Shiksha User" : "Not Shiksha User"}
                                            </span>
                                        </td>
                                        <td className="text-center">
                                            <button
                                                className="btn btn-sm btn-warning"
                                                style={{ fontSize: "12px", fontWeight: 600, whiteSpace: "nowrap" }}
                                                onClick={() => handleEnroll(row)}
                                                disabled={enrolling.has(row.phone)}
                                            >
                                                {enrolling.has(row.phone) ? (
                                                    <><span className="spinner-border spinner-border-sm me-1" />Updating…</>
                                                ) : "Update"}
                                            </button>
                                        </td>
                                        <td style={{ whiteSpace: "nowrap" }}>{fmtDate(row.createdDate)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
